import { t } from './config'
import { escapeHTML } from './richText'
import pencilIcon from '../../../images/icons/pencil-midnight-black.svg'
import lovedIcon from '../../../images/icons/heart-filled-warning.svg'
import okayIcon from '../../../images/icons/circle-minus-midnight-black-80.svg'
import rejectedIcon from '../../../images/icons/circle-cross-midnight-black-80.svg'

// One per chat. Verdicts are kept across re-renders: the same piece in a later result set keeps its verdict.
export function createFeedback (container, submit) {
  const verdicts = new Map()

  function html (listingId) {
    return feedbackHTML(listingId, verdicts.get(String(listingId)))
  }

  function commit (root, state) {
    verdicts.set(root.dataset.listingId, state)
    const panel = root.querySelector('.vjs-vera-feedback-panel')
    root.querySelector('.vjs-vera-feedback-options')?.remove()
    root.querySelector('.vjs-vera-feedback-pill')?.remove()
    panel.insertAdjacentHTML('beforebegin', pillHTML(state))
    closePanel(root)
    submit({ listing_id: Number(root.dataset.listingId), widget_id: widgetId(root), ...state })
  }

  // From the event stream (replay or echo). A control mid-edit is left alone.
  function show ({ listing_id: listingId, verdict, reason, note }) {
    verdicts.set(String(listingId), { verdict, reason: reason || undefined, note: note || undefined })

    container.querySelectorAll(`.vjs-vera-feedback[data-listing-id="${escapeHTML(listingId)}"]`).forEach(root => {
      if (!root.querySelector('.vjs-vera-feedback-panel').hidden) return
      root.outerHTML = html(listingId)
    })
  }

  container.addEventListener('click', event => {
    const verdictButton = event.target.closest('.vjs-vera-verdict')
    if (verdictButton) {
      const root = control(verdictButton)
      const verdict = verdictButton.dataset.verdict
      if (verdict === 'rejected') return openPanel(root)
      return commit(root, { verdict })
    }

    const reasonButton = event.target.closest('.vjs-vera-reason')
    if (reasonButton) {
      const root = control(reasonButton)
      root.querySelectorAll('.vjs-vera-reason').forEach(button => button.classList.remove('is-selected'))
      reasonButton.classList.add('is-selected')
      return syncSubmit(root)
    }

    if (event.target.closest('.vjs-vera-feedback-submit')) {
      const root = control(event.target)
      return commit(root, {
        verdict: 'rejected',
        reason: root.querySelector('.vjs-vera-reason.is-selected')?.dataset.reason,
        note: root.querySelector('.vjs-vera-note').value.trim()
      })
    }

    if (event.target.closest('.vjs-vera-feedback-back')) return closePanel(control(event.target))

    if (event.target.closest('.vjs-vera-feedback-edit')) return reopen(control(event.target))
  })

  return {
    html,
    show,
    reset: () => verdicts.clear()
  }
}

export function feedbackHTML (listingId, state) {
  return `
    <div class="relative vjs-vera-feedback" data-listing-id="${escapeHTML(listingId)}">
      ${state ? pillHTML(state) : optionsHTML()}
      ${panelHTML(state)}
    </div>
  `
}

function optionsHTML () {
  return `
    <div class="vsd-vera-feedback__options vjs-vera-feedback-options">
      ${['loved', 'okay', 'rejected'].map(verdict => `
        <button type="button" class="vsd-vera-feedback__option vjs-vera-verdict" data-verdict="${verdict}">
          <span class="w-16 h-16 border border-midnight-black-40 rounded-full" aria-hidden="true"></span>
          <span>${t(`feedback.${verdict}`)}</span>
        </button>
      `).join('')}
    </div>
  `
}

function pillHTML (state) {
  const reason = state.reason ? `<span class="vsd-vera-feedback__pill-reason">${t(`feedback.reasons.${state.reason}`).toLowerCase()}</span>` : ''

  return `
    <div class="vsd-vera-feedback__pill vjs-vera-feedback-pill">
      ${verdictIcon(state.verdict)}
      <span>${t(`feedback.${state.verdict}`)}</span>
      ${reason}
      <button type="button" class="vsd-vera-feedback__edit vjs-vera-feedback-edit" aria-label="${t('feedback.change')}">
        <img src="${pencilIcon}" alt="">
      </button>
    </div>
  `
}

function verdictIcon (verdict) {
  const icon = { loved: lovedIcon, okay: okayIcon, rejected: rejectedIcon }[verdict]
  return icon ? `<img src="${icon}" width="14" height="14" alt="">` : ''
}

function panelHTML (state) {
  const reasons = ['too_expensive', 'wrong_style', 'too_large', 'wrong_finish', 'other']

  return `
    <div class="vsd-vera-feedback__panel vjs-vera-feedback-panel" hidden>
      <div class="flex items-baseline justify-between mb-12">
        <span class="vsd-vera-feedback__panel-title">${t('feedback.heading')}</span>
        <button type="button" class="vsd-vera-feedback__back vjs-vera-feedback-back">${t('feedback.back')}</button>
      </div>
      <div class="flex flex-wrap gap-8 mb-12">
        ${reasons.map(reason => `
          <button type="button" class="vsd-vera-feedback__reason vjs-vera-reason${state?.reason === reason ? ' is-selected' : ''}" data-reason="${reason}">
            ${t(`feedback.reasons.${reason}`)}
          </button>
        `).join('')}
      </div>
      <textarea class="vsd-vera-feedback__note vjs-vera-note" rows="2" placeholder="${t('feedback.note_placeholder')}">${escapeHTML(state?.note || '')}</textarea>
      <button type="button" class="vsd-vera-feedback__submit vjs-vera-feedback-submit" disabled>${t('feedback.submit')}</button>
    </div>
  `
}

function control (element) {
  return element.closest('.vjs-vera-feedback')
}

function tile (element) {
  return element.closest('.vjs-vera-result')
}

function widgetId (root) {
  return tile(root)?.closest('[data-widget-id]')?.dataset.widgetId
}

function openPanel (root) {
  root.querySelector('.vjs-vera-feedback-panel').hidden = false
  tile(root)?.classList.add('is-reviewing')
  syncSubmit(root)
}

function closePanel (root) {
  root.querySelector('.vjs-vera-feedback-panel').hidden = true
  tile(root)?.classList.remove('is-reviewing')
}

function syncSubmit (root) {
  const chosen = root.querySelector('.vjs-vera-reason.is-selected')
  root.querySelector('.vjs-vera-feedback-submit').disabled = !chosen
}

function reopen (root) {
  root.querySelector('.vjs-vera-feedback-pill')?.remove()
  const panel = root.querySelector('.vjs-vera-feedback-panel')
  panel.insertAdjacentHTML('beforebegin', optionsHTML())
  closePanel(root)
}
